import { useState } from 'react';
import { cs } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Calendar } from '@/shared/ui/shadcn';

interface TermCalendarPickerProps {
  selected: Date[];
  onChange: (dates: Date[]) => void;
}

const navButton =
  'border-phx-border-2 bg-phx-surface text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text inline-flex h-[26px] w-[26px] items-center justify-center rounded-phx border';

// Kompaktní mřížka 3×2 měsíců s pevnou výškou (6 týdnů) – velikost dialogu se výběrem nemění.
const compactClassNames = {
  months: 'grid grid-cols-3 gap-x-2.5 gap-y-1.5',
  month: 'flex w-full flex-col gap-1',
  month_caption: 'flex h-5 items-center justify-center',
  caption_label: 'text-phx-text text-[11px] font-semibold capitalize',
  month_grid: 'w-full border-collapse',
  weekdays: 'flex',
  weekday: 'text-phx-text-muted w-5 text-center text-[9px] font-medium',
  week: 'flex w-full',
  day: 'size-5 p-0 text-center text-[11px]',
  day_button:
    'hover:bg-phx-surface-2 size-5 rounded-[3px] p-0 text-[11px] font-normal aria-selected:opacity-100',
  today: 'bg-phx-surface-2 rounded-[3px] font-semibold',
  selected:
    '[&>button]:bg-phx-primary [&>button]:text-phx-primary-fg [&>button]:hover:bg-phx-primary-hover',
  // Dny mimo daný měsíc (výplň na začátku/konci mřížky) – jen světle naznačené.
  outside: '[&>button]:text-phx-text-faint [&>button]:opacity-50',
};

/**
 * Ruční výběr začátků termínů v kalendáři. Kompaktní mřížka 3×2 měsíců (týden začíná pondělím), vlastní
 * navigace posouvá po jednom měsíci. Konec termínu se dopočítá z délky termínu až v náhledu.
 */
export function TermCalendarPicker({ selected, onChange }: TermCalendarPickerProps) {
  const { t } = useTranslation('planner');
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const shiftMonth = (delta: number) =>
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          className={navButton}
          aria-label={t('serieDetail.generateTerms.calendar.prev')}
          onClick={() => shiftMonth(-1)}
        >
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          className={navButton}
          aria-label={t('serieDetail.generateTerms.calendar.next')}
          onClick={() => shiftMonth(1)}
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <Calendar
        mode="multiple"
        numberOfMonths={6}
        month={month}
        onMonthChange={setMonth}
        hideNavigation
        fixedWeeks
        showOutsideDays
        weekStartsOn={1}
        locale={cs}
        selected={selected}
        onSelect={(dates) => onChange(dates ?? [])}
        className={cn('p-0')}
        classNames={compactClassNames}
      />
      <p className="text-phx-text-muted text-phx-xs">
        {t('serieDetail.generateTerms.calendar.hint')}
      </p>
    </div>
  );
}
